import * as React from 'react'

import { ChevronDownIcon, ChevronUpIcon, PlusIcon } from 'lucide-react'
import { action, makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { ListSelector } from '~/client/src/shared/components/ListSelector'
import { Loader } from '~/client/src/shared/components/Loader'
import LocationEquipmentResourcePicker from '~/client/src/shared/components/LocationPicker/LocationEquipmentResourcePicker'
import ResponsiveSurface from '~/client/src/shared/components/ResponsiveSurface/ResponsiveSurface'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import { Switch } from '~/client/src/shared/components/shadcn-ui/switch'
import StruxhubInput from '~/client/src/shared/components/StruxhubInputs/StruxhubInput'
import StruxhubAttributeSelector from '~/client/src/shared/components/StruxhubInputs/StruxhubSelector/StruxhubAttributeSelector'
import StruxhubTextArea from '~/client/src/shared/components/StruxhubInputs/StruxhubTextArea'
import UserProfilePreview from '~/client/src/shared/components/UserProfilePreview/UserProfilePreview'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import LocationAttributesStore from '~/client/src/shared/stores/domain/LocationAttributes.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import { EMPTY_STRING } from '~/client/src/shared/utils/usefulStrings'

import EventsStore from '../../../../stores/EventStore/Events.store'
import ConfirmDialog from '../../../ConfirmDialog/ConfirmDialog'
import AllowedUsersPicker from '../../AllowedUsersPicker'
import QrCodesStore from '../../QRCodes.store'
import { QRCodesEditFormStore } from './QRCodesEditForm.store'

interface IProps {
  store: QrCodesStore

  eventsStore?: EventsStore
  projectMembersStore?: ProjectMembersStore
  locationAttributesStore?: LocationAttributesStore
  isMobileEnv?: boolean
}

@inject(
  'eventsStore',
  'projectMembersStore',
  'locationAttributesStore',
  'isMobileEnv',
)
@observer
export default class QRCodesEditForm extends React.Component<IProps> {
  private readonly clearPostEventCallback: () => void = null
  private store: QRCodesEditFormStore = null
  // Top of the form: the picker opens from here, over the fields, for the most room.
  private readonly pickerAnchorRef = React.createRef<HTMLDivElement>()

  public constructor(props: IProps) {
    super(props)

    this.store = new QRCodesEditFormStore(
      props.store,
      props.projectMembersStore,
    )

    this.clearPostEventCallback = props.eventsStore.addPostEventCallback(
      this.handlePostEventCallback,
    )
    makeObservable(this)
  }

  public componentWillUnmount(): void {
    this.clearPostEventCallback?.()
  }

  public render(): JSX.Element {
    const { isLoading } = this.props.store

    if (isLoading) {
      return <Loader />
    }

    return (
      <>
        <div className="qr-edit-form col px10 relative full-height">
          <div ref={this.pickerAnchorRef} />
          <div className="col full-height">{this.renderBody()}</div>
        </div>
      </>
    )
  }

  private renderBody() {
    const { isUserDirectoryShown, toggleUserDirectory } = this.store

    if (isUserDirectoryShown) {
      return (
        <AllowedUsersPicker
          appliedUsers={this.store.selectedUsers}
          onClose={toggleUserDirectory}
          onApply={this.handleApplyingAllowedUsers}
        />
      )
    }

    return (
      <>
        {this.renderDeletionConfirmDialog()}
        {this.renderScannerForm()}
      </>
    )
  }

  private renderScannerForm(): JSX.Element {
    return (
      <>
        <div className="col px10 overflow-auto">
          {/* {this.renderScannerHeader()} */}
          {this.renderScannerFields()}
          {this.renderScannerFooter()}
        </div>
      </>
    )
  }

  private renderDeletionConfirmDialog(): JSX.Element {
    const {
      isScannerDeleteModalShown,
      isLoading,
      performDelete,
      toggleDeleteModal,
    } = this.props.store

    return (
      <ConfirmDialog
        useRedConfirmationButton
        title={Localization.translator.deleteScanner}
        isOpen={isScannerDeleteModalShown}
        onCancelClicked={toggleDeleteModal}
        onDoneClicked={performDelete}
        doneTitle={Localization.translator.delete}
        loading={isLoading}
      >
        <div className="text large pre-line">
          {Localization.translator.shouldDeleteScanner}
        </div>
      </ConfirmDialog>
    )
  }

  private renderScannerFooter(): JSX.Element {
    const { isSaveButtonEnabled, newScanner, onSaveClick } = this.props.store

    return (
      <>
        {newScanner && (
          <Button
            variant="primary-outline"
            className="bold pa10 brada10 my10"
            disabled={!isSaveButtonEnabled}
            onClick={onSaveClick}
          >
            {Localization.translator.save}
          </Button>
        )}
      </>
    )
  }

  private renderScannerFields(): JSX.Element {
    const {
      projectMembersStore: { getById },
      store: {
        location,
        onNameChange,
        name,
        maxElapsedTime,
        onMaxElapsedTimeChange,
        badgeName,
        onBNameChange,
        newScanner,
        isOpenScanner,
        isQueueScanner,
        isTimedScanner,
        isTimedByDay,
        toggleIsTimedScanner,
        toggleIsOpenScanner,
        toggleIsTimedByDay,
        toggleIsQueueScanner,
      },
    } = this.props
    const {
      toggleLocationPickerState,
      toggleUserDirectory,
      isUsersListExpanded,
      toggleUsersList,
    } = this.store

    const ChevronIcon = isUsersListExpanded ? ChevronDownIcon : ChevronUpIcon

    return (
      <>
        <StruxhubInput
          label={Localization.translator.name}
          isHelperTextHidden={true}
          isRequired
          value={name}
          onChange={onNameChange}
          className="no-grow"
        />
        <StruxhubTextArea
          className="no-grow"
          label={Localization.translator.badgeDescription}
          isRequired
          isHelperTextCondensed={true}
          value={badgeName || EMPTY_STRING}
          onChange={onBNameChange}
          rows={4}
        />
        <StruxhubAttributeSelector
          onClick={toggleLocationPickerState}
          label={Localization.translator.location}
          value={location?.id}
          className="no-grow"
          isHelperTextHidden={true}
        />
        {this.renderLocationPicker()}
        {isTimedScanner && (
          <StruxhubInput
            label={Localization.translator.maxElapsedTime}
            isHelperTextHidden
            isRequiredTextHidden
            type="number"
            value={`${maxElapsedTime || ''}`}
            onChange={onMaxElapsedTimeChange}
            className="no-grow"
          />
        )}
        <div className="tw:flex tw:items-center tw:justify-between pa10">
          <div className="text left large line-extra-large bold blue-highlight">
            {Localization.translator.limitAccess}
          </div>
          <Switch
            checked={!isOpenScanner}
            onCheckedChange={toggleIsOpenScanner}
            className="no-grow"
          />
        </div>
        <div className="tw:flex tw:items-center tw:justify-between pa10">
          <div className="text left large line-extra-large bold blue-highlight">
            {Localization.translator.makeItTimed}
          </div>
          <Switch
            checked={isTimedScanner}
            onCheckedChange={toggleIsTimedScanner}
            className="no-grow"
          />
        </div>
        {isTimedScanner && (
          <>
            <div className="tw:flex tw:items-center tw:justify-between pa10">
              <div className="text left large line-extra-large bold blue-highlight">
                {Localization.translator.isScannerTimedByDay}
              </div>
              <Switch
                checked={isTimedByDay}
                onCheckedChange={toggleIsTimedByDay}
                className="no-grow"
              />
            </div>
            <div className="tw:flex tw:items-center tw:justify-between pa10">
              <div className="text left large line-extra-large bold blue-highlight">
                {Localization.translator.queue}
              </div>
              <Switch
                checked={isQueueScanner}
                onCheckedChange={toggleIsQueueScanner}
                className="no-grow"
              />
            </div>
          </>
        )}
        {!isOpenScanner && (
          <div className="col py10 no-grow">
            <div className="row bb-light-input-border pa10">
              <div className="text large">
                {Localization.translator.allowedUsers}
              </div>
              <ChevronIcon
                size={16}
                onClick={toggleUsersList}
                className="no-grow pointer"
              />
              <div
                className="no-grow text nowrap large blue bold right pointer pl15"
                onClick={toggleUserDirectory}
              >
                <PlusIcon size={16} className="tw:align-middle" />{' '}
                {Localization.translator.addNew}
              </div>
            </div>
            {isUsersListExpanded && !!newScanner?.allowedUsers?.length && (
              <div className="col overflow-auto bb-light-input-border">
                {newScanner.allowedUsers.map(userId => (
                  <UserProfilePreview
                    key={userId}
                    user={getById(userId)}
                    className="overflow-unset py2"
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </>
    )
  }

  private renderLocationPicker(): JSX.Element {
    const { store, isMobileEnv } = this.props
    const { isLocationPickerShown, hideLocationPicker } = this.store
    const title = Localization.translator.selectAssociatedResource

    return (
      <ResponsiveSurface
        open={isLocationPickerShown}
        onOpenChange={open => !open && hideLocationPicker()}
        title={title}
        isMobileMode={isMobileEnv}
        anchor={this.pickerAnchorRef.current}
        sizing="fill"
      >
        <ListSelector.Title title={title} onClose={hideLocationPicker} />
        <div className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col">
          <LocationEquipmentResourcePicker
            mode="single"
            stageTreeSelection={true}
            allowEmptySelection={true}
            selectedIds={store.location?.id ? [store.location.id] : []}
            onSelect={this.handleLocationSelect}
            onClose={hideLocationPicker}
          />
        </div>
      </ResponsiveSurface>
    )
  }

  private handleLocationSelect = ([id]: string[]): void => {
    const { store, locationAttributesStore } = this.props
    if (id !== store.location?.id) {
      store.onLocationChange(
        id ? locationAttributesStore.getById(id) : undefined,
      )
    }
    this.store.hideLocationPicker()
  }

  private handlePostEventCallback = () => {
    //this.store.onScannerUpdated(eventContext)
  }

  @action.bound
  private handleApplyingAllowedUsers(appliedUsersIds: string[] = []) {
    this.store.onApplyAllowedUsers(appliedUsersIds)

    // To summarize the result of the applying
    this.store.showUsersList()
  }
}
